import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Field,
  reduxForm,
  formValueSelector,
  change as changeFieldValue,
} from "redux-form";
import {
  SelectInput,
  TextInput,
  CheckboxInput,
  StyledCheckboxInput,
} from "../../../../common/form";
import { FormattedMessage, FormattedHTMLMessage, intlShape } from "react-intl";

import { Render } from "../../../../common";
import * as dataActions from "../../../../../utils/data/dataActions";

import { MANUAL } from "../../../clients/enums/billingPeriodTypes";

import validationUtils from "../../../../../utils/ValidationUtils";
import { BILLING_INFO_VALIDATION_RULES } from "./billingInfoValidationRules";

import { EnumWrapper } from "../../../../../utils/enums";
import { PROVINCES, COUNTRIES } from "../../../../../utils/enums/enums";
import { TERMINATED } from "../../enums/clientStatusTypes";
import userHasAccess from "../../../../../utils/userHasAccess";
import { FINTECH } from "../../../../../utils/Roles";

class BasicBillingInfoFields extends React.Component {
  getChildContext() {
    return {
      scene: this.props.scene,
    };
  }

  render() {
    const {
      changeFieldValue,
      clientBillingPeriodType,
      countryCode,
      handleSubmit,
      isArchived,
      clientStatusType
    } = this.props;

    const isClientTerminated = clientStatusType === TERMINATED.value && !userHasAccess(FINTECH);

    return (
      <form onSubmit={handleSubmit}>
        <div className="sub-group">
          <h3>
            <FormattedMessage id="invoices.form.addressSettings.title" />
          </h3>
          <Field
            id="recipient"
            name="recipient"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.recipient" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="address"
            name="address"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.address" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="city"
            name="city"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.city" />}
            disabled={isArchived || isClientTerminated}
          />

          <EnumWrapper enum={COUNTRIES} format="inputs" prop="options">
            <Field
              id="countryCode"
              name="countryCode"
              props={{
                onChange: () => {
                  changeFieldValue("billingInfo", "provinceCode", null);
                },
              }}
              component={SelectInput}
              addBlankOption={true}
              label={<FormattedMessage id="invoices.form.country" />}
              disabled={isArchived || isClientTerminated}
            />
          </EnumWrapper>

          {countryCode && countryCode != " " && (
            <EnumWrapper
              enum={PROVINCES}
              query={`filters=provinces.countryCode==${countryCode}`}
              format="inputs"
              prop="options"
            >
              <Field
                id="provinceCode"
                name="provinceCode"
                component={SelectInput}
                addBlankOption={true}
                noOptionsAction={() => {
                  changeFieldValue(
                    "billingInfo",
                    "provinceCode",
                    "noProvinces"
                  );
                }}
                disabled={isArchived || isClientTerminated}
                label={<FormattedMessage id="invoices.form.province" />}
              />
            </EnumWrapper>
          )}

          <Field
            id="postalCode"
            name="postalCode"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.postalCode" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="phoneNumber1"
            name="phoneNumber1"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.phoneNumber1" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="phoneNumber2"
            name="phoneNumber2"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.phoneNumber2" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="faxNumber"
            name="faxNumber"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.fax" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="emailAddress"
            name="emailAddress"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.emailAddress" />}
            disabled={isArchived || isClientTerminated}
          />
          <Field
            id="websiteAddress"
            name="websiteAddress"
            type="text"
            component={TextInput}
            label={<FormattedMessage id="invoices.form.website" />}
            disabled={isArchived || isClientTerminated}
          />

          <Field
            id="showPaymentNotice"
            name="showPaymentNotice"
            component={CheckboxInput}
            checkbox={{ value: true }}
            label={<FormattedMessage id="invoices.form.showPaymentNotice" />}
            disabled={isArchived || isClientTerminated}
          />
        </div>

        <div className="sub-group">
          <h3>
            <FormattedMessage id="invoices.form.otherSettings.title" />
          </h3>
          <Field
            id="mustUngroupGuarantees"
            name="mustUngroupGuarantees"
            component={StyledCheckboxInput}
            checkbox={{ value: true }}
            label={
              <FormattedMessage id="invoices.form.ungroupGuaranteesInInvoices" />
            }
            disabled={isArchived || isClientTerminated}
          />
          <Render if={MANUAL.value === clientBillingPeriodType}>
            <Field
              id="includeWithdrawalMembers"
              name="includeWithdrawalMembers"
              component={StyledCheckboxInput}
              checkbox={{ value: true }}
              label={
                <FormattedHTMLMessage id="invoices.form.includeWithdrawalMembers" />
              }
              disabled={isArchived || isClientTerminated}
            />
          </Render>
        </div>
      </form>
    );
  }
}

BasicBillingInfoFields.propTypes = {
  changeFieldValue: PropTypes.func,
  clientBillingPeriodType: PropTypes.string,
  countryCode: PropTypes.string,
  dataActions: PropTypes.object,
  initialValues: PropTypes.object,
  scene: PropTypes.string,
  validation: PropTypes.object,
  handleSubmit: PropTypes.func,
  isArchived: PropTypes.bool,
  clientStatusType: PropTypes.string
};

BasicBillingInfoFields.defaultProps = {
  scene: "",
};

BasicBillingInfoFields.contextTypes = {
  getData: PropTypes.func,
  intl: intlShape,
};

BasicBillingInfoFields.childContextTypes = {
  scene: PropTypes.string,
};

function mapStateToProps(state) {
  const selector = formValueSelector("billingInfo");

  return {
    clientBillingPeriodType: state.data.clients.active.clientBillingPeriodType,
    isArchived: state.data.clients.active.isArchived,
    countryCode: selector(state, "countryCode"),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(
  reduxForm({
    form: "billingInfo",
    validate: (values) =>
      validationUtils.validate(values, BILLING_INFO_VALIDATION_RULES),
  })(BasicBillingInfoFields)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/invoices/BasicBillingInfoFields.js